import { getFacility } from '@/apis/application';
import { PlanPremium, PlanData } from '@/types/Plan/PlanData';
import { FacilityState, FacilityResponse } from '@/types/Facility';
import { PlanDataDeductible } from '@/types/Plan';

import { UiState } from '@/types/UiState/UiState';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { getBanner } from '@/apis/admin';

const initialState: UiState = {
  loading: false,
  error: false,
  language: '',
  facilityList: undefined,
  facilityResp: [],
  documentReq: undefined,
  documentResp: [],
  premiumAmountData: {
    planPremium: [],
    applicants: [],
  },
  isEditPlan: false,
};

export const uiStateSlice = createSlice({
  name: 'uiState',
  initialState,
  reducers: {
    resetUiState: () => {
      return initialState;
    },
    routePlanPage: (state, action) => {
      state.isEditPlan = action.payload;
    },
    addFacilityKey: (state, action: PayloadAction<FacilityState>) => {
      state.facilityList = action.payload as FacilityState[];
    },
    updatePlanUiState: (
      state,
      action: PayloadAction<{
        planData: PlanData;
        planDeductible: PlanDataDeductible;
      }>,
    ) => {
      const { applicants } = action.payload.planData as PlanData;

      state.premiumAmountData.planPremium =
        action.payload.planData.planPremium.map((value) => {
          const premiumDeductible = action.payload.planDeductible.planPremium;
          const planDeductible = premiumDeductible.find(
            (p) => p.planCode === value.planCode,
          );
          return {
            planCode: value.planCode,
            monthlyPremium: value.monthlyPremium,
            yearlyPremium: value.yearlyPremium,
            monthlyPremiumDiscount: value.monthlyPremiumDiscount,
            yearlyPremiumDiscount: value.yearlyPremiumDiscount,
            annualSave: value.annualSave,
            deductibleMonthlyPremium: planDeductible
              ? planDeductible.monthlyPremium
              : 0,
            deductibleYearlyPremium: planDeductible
              ? planDeductible.yearlyPremium
              : 0,
            deductibleMonthlyPremiumDiscount: planDeductible
              ? planDeductible.monthlyPremiumDiscount
              : 0,
            deductibleYearlyPremiumDiscount: planDeductible
              ? planDeductible.yearlyPremiumDiscount
              : 0,
            deductibleAnnualSave: planDeductible
              ? planDeductible.annualSave
              : 0,
          };
        });

      state.premiumAmountData.applicants = applicants.map((applicant) => {
        const { applicants } = action.payload
          .planDeductible as PlanDataDeductible;

        const deductibleApplicant = applicants.find(
          (deductibleApp) => deductibleApp.id === applicant.id,
        );
        return {
          id: applicant.id,
          planPremiums: applicant.planPremiums.map((plan) => {
            const deductiblePlan = deductibleApplicant
              ? deductibleApplicant.planPremiums.find(
                  (deductiblePlan: PlanPremium) =>
                    deductiblePlan.planCode === plan.planCode,
                )
              : null;

            return {
              planCode: plan.planCode,
              monthlyPremium: plan.monthlyPremium,
              yearlyPremium: plan.yearlyPremium,
              monthlyPremiumDiscount: plan.monthlyPremiumDiscount,
              yearlyPremiumDiscount: plan.yearlyPremiumDiscount,
              annualSave: plan.annualSave,
              deductibleMonthlyPremium: deductiblePlan
                ? deductiblePlan.monthlyPremium
                : 0,
              deductibleYearlyPremium: deductiblePlan
                ? deductiblePlan.yearlyPremium
                : 0,
              deductibleMonthlyPremiumDiscount: deductiblePlan
                ? deductiblePlan.monthlyPremiumDiscount
                : 0,
              deductibleYearlyPremiumDiscount: deductiblePlan
                ? deductiblePlan.yearlyPremiumDiscount
                : 0,
              deductibleAnnualSave: deductiblePlan.annualSave
                ? deductiblePlan?.annualSave
                : 0,
            };
          }),
        };
      });
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(getFacility.pending, (state) => {
        state.loading = true;
        state.error = false;
      })
      .addCase(getFacility.fulfilled, (state, action) => {
        state.facilityResp = action.payload.data as FacilityResponse[];
        state.loading = false;
        state.error = false;
      })
      .addCase(getFacility.rejected, (state) => {
        state.loading = false;
        state.error = true;
      });
    builder
      .addCase(getBanner.pending, (state) => {
        state.loading = true;
        state.error = false;
      })
      .addCase(getBanner.fulfilled, (state) => {
        state.loading = false;
        state.error = false;
      })
      .addCase(getBanner.rejected, (state) => {
        state.loading = false;
        state.error = true;
      });
  },
});

export const {
  resetUiState,
  routePlanPage,
  addFacilityKey,
  updatePlanUiState,
} = uiStateSlice.actions;
export default uiStateSlice.reducer;
